<template>
  <div class="timeline-chart">
    <h3>Payment Timeline</h3>

    <div v-if="timelineData.length === 0" class="no-data">
      <p>No credit purchases scheduled</p>
    </div>

    <div v-else class="timeline-scroll">
      <div class="timeline-container">
        <div class="timeline-line"></div>

        <div v-for="(day, index) in timelineData" :key="index" class="timeline-day">
          <div class="day-marker" :class="{ 'past': isPast(day.date) }"></div>

          <div class="day-card" :class="{ 'overdue': isPast(day.date) }">
            <div class="day-header">
              <span class="day-date">{{ day.dateFormatted }}</span>
              <span class="day-count">{{ day.count }}</span>
            </div>

            <div class="day-amount">{{ formatCurrency(day.amount) }}</div>

            <div class="day-details">
              <small v-for="purchase in day.purchases.slice(0, 2)" :key="purchase.id" class="purchase-item">
                {{ purchase.purchaseNo }}
              </small>
              <small v-if="day.purchases.length > 2" class="more-purchases">
                +{{ day.purchases.length - 2 }} more
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="timelineData.length > 0" class="timeline-stats">
      <div class="stat">
        <label>Total Days</label>
        <span class="value">{{ timelineData.length }}</span>
      </div>
      <div class="stat">
        <label>Total Amount</label>
        <span class="value">{{ formatCurrency(totalAmount) }}</span>
      </div>
      <div class="stat">
        <label>Peak Day</label>
        <span class="value">{{ peakDate?.dateFormatted || 'N/A' }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue';

const props = defineProps({
  timelineData: {
    type: Array,
    default: () => [],
  },
});

const formatCurrency = (value) => {
  if (!value) return '₦0.00';
  return `₦${(value / 1000).toFixed(1)}K`;
};

const isPast = (date) => {
  return new Date(date) < new Date();
};

const totalAmount = computed(() => {
  return props.timelineData.reduce((sum, day) => sum + (day.amount || 0), 0);
});

const peakDate = computed(() => {
  return props.timelineData.reduce((max, day) =>
    (day.count || 0) > (max.count || 0) ? day : max,
  );
});
</script>

<script>
export default {
  name: 'CreditTimelineChart',
};
</script>

<style scoped>
.timeline-chart {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.timeline-chart h3 {
  margin: 0 0 20px 0;
  color: #333;
  font-size: 18px;
  font-weight: 600;
}

.no-data {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.timeline-scroll {
  flex: 1;
  overflow-x: auto;
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.timeline-container {
  display: flex;
  gap: 15px;
  min-height: 200px;
  position: relative;
  padding: 10px 0;
}

.timeline-line {
  position: absolute;
  left: 0;
  right: 0;
  top: 30px;
  height: 2px;
  background: linear-gradient(90deg, #ddd 0%, #f0f0f0 50%, #ddd 100%);
  z-index: 0;
}

.timeline-day {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  width: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.day-marker {
  width: 20px;
  height: 20px;
  background: #667eea;
  border: 3px solid white;
  border-radius: 50%;
  margin-bottom: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 2;
}

.day-marker.past {
  background: #ff6b6b;
}

.day-card {
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 12px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.day-card:hover {
  border-color: #667eea;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
}

.day-card.overdue {
  background: #fff5f5;
  border-color: #ff6b6b;
}

.day-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
}

.day-date {
  font-size: 12px;
  color: #666;
  font-weight: 600;
  flex: 1;
}

.day-count {
  background: #f0f0f0;
  color: #333;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 700;
  min-width: 24px;
}

.day-amount {
  font-weight: 700;
  color: #27ae60;
  font-size: 13px;
  margin-bottom: 8px;
}

.day-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.purchase-item {
  color: #667eea;
  font-size: 10px;
  font-weight: 600;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.more-purchases {
  color: #999;
  font-size: 10px;
  font-style: italic;
}

.timeline-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  padding-top: 10px;
}

.stat {
  text-align: center;
  padding: 10px;
  background: #f9f9f9;
  border-radius: 4px;
}

.stat label {
  display: block;
  color: #666;
  font-size: 12px;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat .value {
  display: block;
  color: #333;
  font-size: 16px;
  font-weight: 700;
}

@media (max-width: 768px) {
  .timeline-scroll {
    max-height: 250px;
  }

  .timeline-stats {
    grid-template-columns: 1fr;
  }
}
</style>
